import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useRouter } from '@tanstack/react-router';
import {
  ArrowLeft,
  Check,
  Eye,
  FileSpreadsheet,
  Inbox,
  Loader2,
  Printer,
  Send,
  TriangleAlert,
} from 'lucide-react';
import { DataTable, DataTablePagination } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { PageLoader } from '@/components/common/PageLoader';
import { Button, buttonVariants } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useKeepPageInRange } from '@/hooks/useKeepPageInRange';
import { cn } from '@/lib/utils';
import { formatNumber, formatTaka, pluralize } from '@/utils/number';
import { PcmpHoldActionButtons } from '../components/PcmpHoldActions/PcmpHoldActionButtons';
import { PCMP_PERMISSION } from '../api/pcmpAccess.api';
import {
  downloadPcmpAccountsBatchXlsx,
  pcmpAccountsBatchQueryOptions,
} from '../api/pcmpAccounts.api';
import {
  PcmpBatchAmountFact,
  PcmpBatchFact,
  PcmpPaymentTypeStrip,
} from '../components/PcmpAccountsBatch/PcmpAccountsBatchFacts';
import { PcmpAccountsActionButtons } from '../components/PcmpAccountsActions/PcmpAccountsActionButtons';
import {
  PcmpAccountsBulkDialog,
  type PcmpAccountsBulkAction,
  type PcmpAccountsBulkTarget,
} from '../components/PcmpAccountsActions/PcmpAccountsBulkDialog';
import { getPcmpAccountsColumns } from '../components/PcmpAccountsQueue/PcmpAccountsColumns';
import { PcmpSelectionBar } from '../components/PcmpBatch/PcmpSelectionBar';
import { PcmpBatchStepCell } from '../components/PcmpBatch/PcmpBatchCells';
import { PcmpStatusPill } from '../components/PcmpProposalDetail/PcmpStatusPill';
import { PcmpQueueFilterBar } from '../components/PcmpQueue/PcmpQueueFilterBar';
import { pcmpSelectColumn } from '../components/PcmpQueue/pcmpSelectColumn';
import {
  PCMP_ACCOUNTS_BATCH_KIND_LABELS,
  PCMP_ACCOUNTS_ROLL_UP_TONE,
} from '../constants/pcmpAccounts.constants';
import { PCMP_QUEUE_PAGE_SIZE_OPTIONS } from '../constants/pcmpQueue.constants';
import { useDownloadPcmpBatchXlsx } from '../hooks/useDownloadPcmpBatchXlsx';
import { usePcmpAccountsBatchFilters } from '../hooks/usePcmpAccountsBatchFilters';
import { usePcmpPermissions } from '../hooks/usePcmpPermissions';
import { usePcmpRowSelection } from '../hooks/usePcmpRowSelection';
import type { PcmpAccountsBatch, PcmpAccountsBatchItem } from '../types/pcmpAccounts.types';

// Only pending, unheld items can be ticked: both bulk actions act on those alone.
const disburseBlockedReason = (item: PcmpAccountsBatchItem) =>
  item.canDisburse ? null : (item.disburseBlockedReason ?? 'Not awaiting disbursement.');

/** The Accounts tab a batch is listed on, for the way back. */
function backSearch(batch: PcmpAccountsBatch | undefined) {
  if (batch?.kind === 'URGENT') {
    return {
      tab: 'URGENT' as const,
      urgentView: batch.pending.count > 0 ? undefined : ('DISBURSED' as const),
    };
  }
  if (batch?.pending.count === 0 && batch.disbursed.count > 0) {
    return { tab: 'DISBURSED_BATCHES' as const };
  }
  return {};
}

/** One route or urgent batch: its facts, payment-type split, members and payment actions. */
export function PcmpAccountsBatchPage() {
  const router = useRouter();
  const controller = usePcmpAccountsBatchFilters();
  const { batchNo, params, appliedFilters, showAll, limit } = controller;
  const { can } = usePcmpPermissions();
  const canExport = can(PCMP_PERMISSION.VIEW_ALL, PCMP_PERMISSION.EXPORT);
  // A hint only: each row's capabilities and the API decide.
  const canAct = can(PCMP_PERMISSION.ACCOUNTS, PCMP_PERMISSION.ACT);

  const selection = usePcmpRowSelection(JSON.stringify([batchNo, appliedFilters, showAll]));
  const [bulkTarget, setBulkTarget] = useState<PcmpAccountsBulkTarget | null>(null);
  const xlsx = useDownloadPcmpBatchXlsx((date) => downloadPcmpAccountsBatchXlsx(batchNo, date));

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    pcmpAccountsBatchQueryOptions(batchNo, params),
  );

  useKeepPageInRange(params.page, data?.proposals.meta.totalPages, controller.setPage);

  const rows = useMemo(() => data?.proposals.items ?? [], [data]);
  const batch = data?.batch;

  const openProposal = useCallback(
    (item: PcmpAccountsBatchItem) => {
      void router.navigate({
        to: '/pcmp/proposals/$proposalId',
        params: { proposalId: item.proposalId },
      });
    },
    [router],
  );

  const backButton = (
    <Link
      to="/pcmp/accounts"
      search={backSearch(batch)}
      className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'w-fit gap-1.5')}
    >
      <ArrowLeft className="size-3.5" />
      Batches
    </Link>
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-5">
        {backButton}
        <PageLoader />
      </div>
    );
  }

  if (error || !data || !batch) {
    return (
      <div className="flex flex-col gap-5">
        {backButton}
        <ErrorState
          error={error}
          fallbackTitle={`Could not load batch #${batchNo}`}
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          fill
        />
      </div>
    );
  }

  const canSelect = canAct && batch.pending.count > 0;
  const columns = getPcmpAccountsColumns<PcmpAccountsBatchItem>({
    leading: canSelect
      ? [
          pcmpSelectColumn<PcmpAccountsBatchItem>({
            selection,
            blockedReason: disburseBlockedReason,
          }),
        ]
      : [],
    renderActions: (item) => (
      <>
        {canAct ? (
          <PcmpAccountsActionButtons
            proposalId={item.proposalId}
            refNo={item.refNo}
            fail={item.canFail ? { stage: item.failureStage } : null}
            canReturn={item.canReturn}
            className="flex-nowrap"
          />
        ) : null}
        <PcmpHoldActionButtons
          proposalId={item.proposalId}
          refNo={item.refNo}
          canHold={item.canHold}
          canRelease={item.canRelease}
          hold={item.hold}
          className="flex-nowrap"
        />
        <Link
          to="/pcmp/proposals/$proposalId"
          params={{ proposalId: item.proposalId }}
          className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'gap-1.5')}
        >
          <Eye className="size-3.5" />
          View
        </Link>
      </>
    ),
  });

  // "Select all" covers the pending items the filters match, or the whole batch's.
  const selectable = data.isFiltered && data.matchedPending ? data.matchedPending : batch.pending;
  const summary = selection.summarize(selectable.count, selectable.amount);
  const pageSelectable = rows.filter((row) => row.canDisburse);
  const isPageSelected =
    pageSelectable.length > 0 &&
    pageSelectable.every((row) => selection.isSelected(row.proposalId));
  const refNoOf = (proposalId: number) => rows.find((row) => row.proposalId === proposalId)?.refNo;

  const startBulk = (action: PcmpAccountsBulkAction) =>
    setBulkTarget({
      action,
      batchNo,
      count: summary.count,
      amount: summary.amount,
      scope: selection.allMatching
        ? data.isFiltered
          ? 'Pending proposals matching the filters'
          : 'All pending proposals in the batch'
        : 'Selected proposals',
      selection: selection.allMatching
        ? {
            selectAll: data.isFiltered ? 'MATCHING' : 'BATCH',
            filters: data.isFiltered ? appliedFilters : undefined,
            excludedProposalIds: selection.pickedIds,
          }
        : { proposalIds: selection.pickedIds },
    });

  const disburseWholeBatch = () =>
    setBulkTarget({
      action: 'DISBURSE',
      batchNo,
      count: batch.pending.count,
      amount: batch.pending.amount,
      scope: 'All pending proposals in the batch',
      selection: { selectAll: 'BATCH', excludedProposalIds: [] },
    });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          {backButton}
          <h1 className="text-2xl font-semibold">Batch #{batch.batchNo}</h1>
          <PcmpStatusPill tone={PCMP_ACCOUNTS_ROLL_UP_TONE[batch.rollUp]}>
            {batch.rollUpLabel}
          </PcmpStatusPill>
          <span className="text-sm text-muted-foreground">
            {PCMP_ACCOUNTS_BATCH_KIND_LABELS[batch.kind]}
          </span>
        </div>
        {canExport || canSelect ? (
          <div className="flex flex-wrap items-center gap-2">
            {canExport ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => xlsx.mutate()}
                  disabled={xlsx.isPending}
                  className="gap-1.5"
                >
                  {xlsx.isPending ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="size-3.5" />
                  )}
                  Excel
                </Button>
                <Link
                  to="/pcmp/accounts/batches/$batchNo/print"
                  params={{ batchNo }}
                  target="_blank"
                  className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'gap-1.5')}
                >
                  <Printer className="size-3.5" />
                  Print / PDF
                </Link>
              </>
            ) : null}
            {canSelect ? (
              <Button type="button" size="sm" onClick={disburseWholeBatch} className="gap-1.5">
                <Send className="size-3.5" />
                Disburse Batch ({formatNumber(batch.pending.count)})
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
        <PcmpBatchFact label="Proposals">
          <span className="text-lg font-semibold tabular-nums">
            {formatTaka(batch.totalAmount)}
          </span>
          <span className="text-xs text-muted-foreground tabular-nums">
            {formatNumber(batch.itemCount)} {pluralize(batch.itemCount, 'proposal')}
          </span>
        </PcmpBatchFact>
        <PcmpBatchAmountFact label="Pending" value={batch.pending} />
        <PcmpBatchAmountFact label="Disbursed" value={batch.disbursed} />
        <PcmpBatchAmountFact label="Failed" value={batch.disbursementFailed} />
        <PcmpBatchAmountFact label="Returned" value={batch.returned} />
        <PcmpBatchAmountFact label="On Hold" value={batch.held} />
        <PcmpBatchFact label="Published">
          <PcmpBatchStepCell by={batch.publishedBy} at={batch.publishedAt} />
        </PcmpBatchFact>
      </div>

      <PcmpPaymentTypeStrip totals={data.paymentTypes} />

      <PcmpQueueFilterBar controller={controller} idPrefix="pcmp-accounts-batch" />

      {data.isFiltered || showAll ? (
        <div className="flex items-center gap-2 text-sm">
          <Checkbox
            id="pcmp-accounts-batch-show-all"
            checked={showAll}
            onCheckedChange={(checked) => controller.setShowAll(checked)}
          />
          <Label htmlFor="pcmp-accounts-batch-show-all" className="font-normal">
            Show all {formatNumber(batch.itemCount)} proposals in the batch, not only the{' '}
            {formatNumber(batch.matchedItemCount ?? data.proposals.meta.total)} matching the filters
          </Label>
        </div>
      ) : null}

      {canSelect ? (
        <PcmpSelectionBar
          selection={selection}
          summary={summary}
          selectAll={{ total: selectable.count, amount: selectable.amount, isPageSelected }}
          hint="Tick pending proposals to disburse them, or to mark them Disbursement Failed before payout."
        >
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => startBulk('FAIL')}
            disabled={summary.count === 0}
            className="gap-1.5 text-destructive hover:text-destructive"
          >
            <TriangleAlert className="size-3.5" />
            Disbursement Failed
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => startBulk('DISBURSE')}
            disabled={summary.count === 0}
            className="gap-1.5"
          >
            <Check className="size-3.5" />
            Disburse Selected
          </Button>
        </PcmpSelectionBar>
      ) : null}

      <DataTable
        data={rows}
        columns={columns}
        isLoading={false}
        stickyFirstColumn
        className={cn(isFetching && 'opacity-70')}
        emptyIcon={Inbox}
        emptyMessage={
          data.isFiltered
            ? 'No proposals in this batch match the filters'
            : 'No proposals in this batch are routed to Accounts'
        }
        emptyHint={
          data.isFiltered
            ? 'Show all proposals in the batch, or change the filters.'
            : 'Its proposals were routed to Marketing.'
        }
        skeletonRowCount={Math.min(limit, 15)}
        getRowClassName={(item) =>
          canSelect && item.canDisburse && selection.isSelected(item.proposalId)
            ? 'bg-primary/5'
            : item.hold
              ? 'bg-warning/20'
              : undefined
        }
        onRowClick={openProposal}
      />

      <DataTablePagination
        page={data.proposals.meta.page}
        totalPages={data.proposals.meta.totalPages}
        total={data.proposals.meta.total}
        limit={data.proposals.meta.limit}
        onPageChange={controller.setPage}
        limitSelect={{ options: PCMP_QUEUE_PAGE_SIZE_OPTIONS, onChange: controller.setLimit }}
      />

      {bulkTarget ? (
        <PcmpAccountsBulkDialog
          target={bulkTarget}
          refNoOf={refNoOf}
          onDone={selection.clear}
          onClose={() => setBulkTarget(null)}
        />
      ) : null}
    </div>
  );
}
